import cloneDeep from 'lodash/cloneDeep';

import isEqual from 'lodash/isEqual';
import { TreeNode } from 'src/components/Tree/type';
import i18nInst from 'src/i18n';

import {
  COLLABORATION_POLICY_TYPE_ID_NAME_MAP,
  COLLABORATION_POLICY_TYPE_IDS,
  MAIL_POLICY_TYPE_ID_NAME_MAP,
  MAIL_POLICY_TYPE_IDS,
  PolicyTypeEnum,
} from './constants';
import {
  ActionEnum,
  ApprovedHeader,
  CollaborationPolicyNameEnum,
  DlpPolicyFilters,
  DlpPolicyItem,
  DpFilter,
  EnableDisableEnum,
  ErrorObj,
  ExchangeOnlinePolicyItem,
  MailPolicyNameEnum,
  Notification,
  PolicyFilters,
  PolicyHolderTypeEnum,
  PolicyItem,
  PolicyItemInTable,
  TargetGroup,
  TargetUser,
} from './types';
import { Key } from 'react';
import common, { isInUIC } from '../../../utils/common';
import { IS_UAE } from '@Utils/const';
import { PolicyType, Service } from '../../../types/app';
import config from 'src/config';
import { BlockedHeaderRuleDefinitionsProps } from '@Components/BlockedHeaderFieldList';
import { UserIcon, UserTeamIcon } from '@tonic-one/react-icons';
import { TeamsIcon } from '@Policy/Components/TeamsIcon';

export const checkManualScanPending = function (status: string) {
  return ['U', 'A', 'I'].indexOf(status) !== -1;
};
export const checkManualScanFinished = function (status: string) {
  return ['C', 'B', 'F'].indexOf(status) !== -1;
};
export const checkManualScanCancelling = function (status: string) {
  return status === 'I';
};
export const checkManualScanCanceled = function (status: string) {
  return status === 'B';
};
export const checkManualScanCompleted = function (status: string) {
  return status === 'C';
};
export const checkManualScanFailed = function (status: string) {
  return status === 'F';
};
export const checkManualScanInit = function (status: string) {
  return status === 'U';
};

export const checkScanShow = function (policyType: string) {
  return (
    ['1', '2', '3', '4', '5', '6', '21', '41', '42', '51'].indexOf(policyType) >
    -1
  );
};

export const getPolicyName = function (
  type: number | PolicyType,
  subType?: number
): MailPolicyNameEnum | CollaborationPolicyNameEnum | null {
  if (COLLABORATION_POLICY_TYPE_IDS.includes(`${type}`)) {
    return COLLABORATION_POLICY_TYPE_ID_NAME_MAP[type];
  } else if (MAIL_POLICY_TYPE_IDS.includes(`${type}`)) {
    const v = MAIL_POLICY_TYPE_ID_NAME_MAP[type];
    if (typeof v === 'string') {
      return v;
    } else {
      if (typeof subType !== 'undefined' && [0, 1].includes(subType)) {
        return v[subType as 0 | 1];
      }
    }
  }
  return null;
};

export const checkFileBlockingExtension = function (str: string) {
  if (!str) {
    return false;
  }

  if (str.indexOf('.') === 0 || str.indexOf('.') === str.length - 1) {
    return false;
  }

  const invalid_ext = /[/>?:<"|\\]/;
  if (invalid_ext.test(str)) {
    return false;
  }

  return true;
};
export const checkExtension = (str: string) => {
  if (!str) {
    return false;
  }
  const invalid_ext = /[/>?:<"|\\]/;
  if (str.indexOf('.') === 0 || str.indexOf('.') === str.length - 1) {
    return false;
  }
  if (invalid_ext.test(str)) {
    return false;
  }
  return true;
};
const charMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#039;',
  '"': '&quot;',
  '/': '&#047;',
};
export const char2entity = function (str: string) {
  if (!str || typeof str === 'undefined') return str;
  // for (const reg in charMap) {
  //   str = str.replace(new RegExp(reg, 'g'), charMap[reg as keyof charMap]);
  // }
  Object.keys(charMap).forEach((reg) => {
    str = str.replace(
      new RegExp(reg, 'g'),
      charMap[reg as keyof typeof charMap]
    );
  });
  return str;
};
export const checkEmailFormat = (emailStr: string, limit = 256) => {
  const emailFormat =
    /^([a-zA-Z0-9!#$%&'+\-/=?^_`{|}~.]{1,64})@([a-zA-Z0-9]([a-zA-Z0-9^_.]{1,64})?([-a-zA-Z0-9]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,13}$/;
  const emailBadFormat = /^(\.)|(\.{2,})|(@\.)|(\.@)/;
  if (!emailStr) {
    return false;
  }
  if (emailBadFormat.test(emailStr)) {
    return false;
  }
  if (emailStr.length >= limit) {
    return false;
  }
  if (emailFormat.test(emailStr)) {
    return true;
  }
  return false;
};
export const checkDomainFormat = (domainStr: string, limit = 256): boolean => {
  const domainFormat = new RegExp(
    /^([a-zA-Z0-9]([-a-zA-Z0-9_]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,13}$/
  );
  const badFormat = /[*()@?:]/;
  if (!domainStr) {
    return false;
  }
  if (domainStr.length > limit) {
    return false;
  }
  if (badFormat.test(domainStr)) {
    return false;
  }
  if (domainFormat.test(domainStr)) {
    return true;
  }
  return false;
};
export const generalExport = async ({
  entries,
  name,
}: {
  entries: string[];
  name: string;
}) => {
  try {
    if (entries.length > 0) {
      let content = '';
      entries.forEach(function (val) {
        content += val + '\r\n';
      });
      common.exportFile(`${name}.txt`, content, 'text/plain');
    }
  } catch (error: any) {
    const code = error?.data?.status || 'Unknown';
    window.showNotification({
      titleMsg: `export fail`,
      bodyMsg:
        [404004].indexOf(code) >= 0 ? `export fail ${code}` : `export fail`,
      severity: 'error',
    });
  }
};
export function addItems(
  items: string[] | string,
  current: string,
  listLimit = -1, // limit === -1 means no limit
  itemLimit = -1,
  checkFunction: (str: string) => boolean = checkExtension,
  inSeparator = ';', // default separator is ';'
  outSeparator = '|' // default separator is '|'
) {
  const list = current
    .split(outSeparator)
    .filter((item: string) => item.length > 0);
  const newItems = Array.isArray(items) ? items : items.split(inSeparator);
  const errList: string[] = [];
  let exceedLimit = false;
  const validItems = newItems.filter((item: string) => item.length > 0);
  if (validItems.length === 0) {
    return {
      result: current,
      errList,
    };
  }
  validItems.forEach((item) => {
    if (listLimit !== -1 && list.length >= listLimit) {
      errList.push(item);
      exceedLimit = true;
    } else if (
      !checkFunction(item) ||
      (itemLimit !== -1 && item.length > itemLimit)
    ) {
      errList.push(item);
    } else {
      if (!list.includes(item)) {
        list.push(item);
      }
    }
  });
  const result = list.sort().join(outSeparator);
  return {
    result,
    errList,
    exceedLimit,
  };
}
export function removeItem(item: string, current: string, outSeparator = '|') {
  const validItem = item.trim();
  if (validItem.length === 0) {
    return current;
  }
  const list = current
    .split(outSeparator)
    .filter((item: string) => item.length > 0);

  const index = list.findIndex(
    (listItem: string) => listItem.trim() === validItem
  );

  if (index > -1) {
    list.splice(index, 1);
  }
  return list.join(outSeparator);
}

export function replaceApprovedUrlForAdd(url: string) {
  let newUrl = url.trim();
  if (newUrl !== '') {
    newUrl = newUrl.replace('https://', '').replace('http://', '');
    // for blocked url do not remove parameters
    // if (newUrl.indexOf('?') > -1)
    //     newUrl = newUrl.substring(0, newUrl.indexOf('?'));
    return newUrl;
  } else {
    return newUrl;
  }
}

export function removeProtocol(url: string) {
  if (url.indexOf('://') !== -1) {
    return url.substring(url.indexOf('://') + 3);
  }
  return url;
}
export function checkUrlPrefix(prefixUrl: string) {
  if (prefixUrl.length === 0) {
    return {
      status: 'error',
      data: prefixUrl,
    };
  }
  if (prefixUrl.indexOf('*') === prefixUrl.lastIndexOf('*')) {
    const ipv6url = /^\[([a-zA-z0-9:]+)\]/; // http://[FEDC:BA98:7654:3210:FEDC:BA98:7654:3210]:80/index.html
    if (ipv6url.test(prefixUrl)) {
      prefixUrl = RegExp.$1;
    }
    const urlPort = /^([-a-zA-z0-9.]+):\d+/;
    if (urlPort.test(prefixUrl)) {
      prefixUrl = RegExp.$1;
    }
    return prefixUrl;
  } else {
    return {
      status: 'error',
      data: prefixUrl,
    };
  }
}

/*
Example: news/cn/today, news/cn/today/*
 */
export function checkUrlSuffix(suffixUrl: string) {
  if (suffixUrl.length === 0 || suffixUrl === '/') {
    return suffixUrl;
  }
  if (suffixUrl.indexOf('*') === suffixUrl.lastIndexOf('*')) {
    // Based on RFC 3986, sections only do not allow '?#', as '*' has special meaning in CAS, current we do not support it in Path's section unless in the last kike xxxx/test/*
    if (
      suffixUrl.indexOf('*') !== -1 &&
      suffixUrl.indexOf('*') !== suffixUrl.length - 1
    ) {
      return {
        status: 'error',
        data: suffixUrl,
      };
    }
    // split suffix url with '/'
    const sections = suffixUrl.split('/');
    // example.com, example.com/test/*, example.com/*, example.com/index.html
    if (
      suffixUrl.endsWith('/') ||
      suffixUrl.endsWith('/*') ||
      (sections.length === 1 && suffixUrl === '*')
    ) {
      sections.length = sections.length - 1;
    }
    // check each section
    // Refer https://github.com/ogt/valid-url/blob/master/index.js
    const regexStr = /[^?*#]+$/;
    for (const section of sections) {
      if (!regexStr.test(section)) {
        return {
          status: 'error',
          data: suffixUrl,
        };
      }
    }
    return suffixUrl;
  }
  return {
    status: 'error',
    data: suffixUrl,
  };
}

export function checkUrlQueryString(queryString: string) {
  if (queryString.length !== 0 && queryString.indexOf('*') !== -1) {
    return {
      status: 'error',
      data: queryString,
    };
  }
  return queryString;
}

export function replaceBlockedUrlForAdd(url: string) {
  url = url.trim();
  if (url !== '') {
    url = url.replace('https://', '').replace('http://', '');
    // for blocked url do not remove parameters
    // if (url.indexOf('?') > -1)
    //     url = url.substring(0, url.indexOf('?'));
    return url;
  } else {
    return url;
  }
}

export const checkIsIPv6Address = function (ipstr: string) {
  if (
    ipstr.match(
      /^((([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}:[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){5}:([0-9A-Fa-f]{1,4}:)?[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){4}:([0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){3}:([0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){2}:([0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(([0-9A-Fa-f]{1,4}:){0,5}:((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(::([0-9A-Fa-f]{1,4}:){0,5}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|([0-9A-Fa-f]{1,4}::([0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})|(::([0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){1,7}:)|(::))$/
    ) === null
  ) {
    return false;
  }
  return true;
};
export const checkIsIPv4Address = function (ipstr: string) {
  const re = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  if (re.test(ipstr)) {
    re.exec(ipstr);
    if (
      Number(RegExp.$1) >= 1 &&
      Number(RegExp.$1) <= 255 &&
      Number(RegExp.$2) >= 0 &&
      Number(RegExp.$2) <= 255 &&
      Number(RegExp.$3) >= 0 &&
      Number(RegExp.$3) <= 255 &&
      Number(RegExp.$4) >= 0 &&
      Number(RegExp.$4) <= 255 &&
      ipstr !== '0.0.0.0' &&
      ipstr !== '255.255.255.255'
    ) {
      return true;
    }
  }
  return false;
};
//domain names could have '_' https://stackoverflow.com/questions/2180465/can-domain-name-subdomains-have-an-underscore-in-it
export const checkUrlFormat = function (urlStr: string, limit = 256) {
  const urlFormat =
    /^([a-zA-Z0-9]([-a-zA-Z0-9_]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,13}$/;
  const urlFormat2 =
    /^\*\.([_a-zA-Z0-9]([-a-zA-Z0-9_]{0,61}[a-zA-Z0-9])?\.){0,}[a-zA-Z0-9]{2,13}$/;
  if (!urlStr) {
    return false;
  }
  return (
    (urlFormat.test(urlStr) || urlFormat2.test(urlStr)) &&
    urlStr.length <= limit
  );
};
/*
export function addUrlToList(
  urls: string[],
  entries: string[],
  listLimit = 1024,
  limit = 256
) {
  const errList: string[] = [];
  let exceedLimit = false;
  urls.forEach((newUrl) => {
    if (entries.length > listLimit) {
      errList.push(newUrl);
      exceedLimit = true;
    } else {
      let prefixUrl = newUrl;
      let suffixUrl = '';
      let queryUrl = '';

      const index = newUrl.indexOf('/');
      const queryIndex = newUrl.indexOf('?');
      // special sample for 'test', 'test.com'
      if (index !== -1) {
        // split url with first slash, check each part with different logic
        prefixUrl = newUrl.substring(0, index);
        if (queryIndex !== -1) {
          suffixUrl = newUrl.substring(index + 1, queryIndex);
          queryUrl = newUrl.substring(queryIndex + 1, newUrl.length);
        } else {
          suffixUrl = newUrl.substring(index + 1, newUrl.length);
        }
      }
      const prefix = checkUrlPrefix(prefixUrl);
      const suffix = checkUrlSuffix(suffixUrl);
      const queryString = checkUrlQueryString(queryUrl);
      if (
        (typeof prefix !== 'string' && prefix.status === 'error') ||
        (typeof suffix !== 'string' && suffix.status === 'error') ||
        (typeof queryString !== 'string' && queryString.status === 'error')
      ) {
        errList.push(newUrl);
      } else {
        let finalUrl = index > -1 ? prefix + '/' + suffix : (prefix as string);
        if (
          checkIsIPv6Address(prefix as string) ||
          checkIsIPv4Address(prefix as string)
        ) {
          finalUrl = queryIndex > -1 ? finalUrl + '?' + queryString : finalUrl;
          if (
            entries &&
            entries !== null &&
            entries.indexOf(finalUrl.toLowerCase()) === -1
          ) {
            entries.push(finalUrl.toLowerCase());
          }
        } else {
          let prefixStr = prefix as string;
          if (prefixStr.indexOf(':') >= 0) {
            prefixStr = prefixStr.split('|')[0];
          }
          prefixStr = prefixStr.replace(/^www\./i, '');
          // let noneStarPrefix = "";
          // if (prefix.startsWith('*.')) {
          //     noneStarPrefix = prefix.substring(2, prefix.length);
          // }
          if (checkUrlFormat(prefixStr, limit)) {
            finalUrl =
              queryIndex > -1 ? finalUrl + '?' + queryString : finalUrl;
            if (
              entries &&
              entries !== null &&
              entries.indexOf(finalUrl.toLowerCase()) === -1
            ) {
              entries.push(finalUrl.toLowerCase());
            }
          } else {
            errList.push(newUrl);
          }
        }
      }
    }
  });
  return {
    errList,
    entries,
    exceedLimit,
  };
}
*/
export function addUrlToList(
  urls: string[],
  entries: string[],
  listLimit = 1024,
  limit = 256
) {
  const errList: string[] = [];
  let exceedLimit = false;
  for (const newUrl of urls) {
    if (entries.length > listLimit) {
      errList.push(newUrl);
      exceedLimit = true;
      continue;
    }

    let prefixUrl = newUrl;
    let suffixUrl = '';
    let queryUrl = '';

    const index = newUrl.indexOf('/');
    const queryIndex = newUrl.indexOf('?');
    // special sample for 'test', 'test.com'
    if (index !== -1) {
      // split url with first slash, check each part with different logic
      prefixUrl = newUrl.substring(0, index);
      if (queryIndex !== -1) {
        suffixUrl = newUrl.substring(index + 1, queryIndex);
        queryUrl = newUrl.substring(queryIndex + 1, newUrl.length);
      } else {
        suffixUrl = newUrl.substring(index + 1, newUrl.length);
      }
    }

    const prefix = checkUrlPrefix(prefixUrl);
    const suffix = checkUrlSuffix(suffixUrl);
    const queryString = checkUrlQueryString(queryUrl);

    if (
      (typeof prefix !== 'string' && prefix.status === 'error') ||
      (typeof suffix !== 'string' && suffix.status === 'error') ||
      (typeof queryString !== 'string' && queryString.status === 'error')
    ) {
      errList.push(newUrl);
      continue;
    }

    let finalUrl: string;
    const isIPv6 = checkIsIPv6Address(prefix as string);
    const isIPv4 = checkIsIPv4Address(prefix as string);

    if (isIPv6 || isIPv4) {
      finalUrl = index > -1 ? `${prefix}/${suffix}` : (prefix as string);
      finalUrl = queryIndex > -1 ? `${finalUrl}?${queryString}` : finalUrl;

      if (!entries || entries.indexOf(finalUrl.toLowerCase()) === -1) {
        entries.push(finalUrl.toLowerCase());
      }
    } else {
      let prefixStr = prefix as string;
      if (prefixStr.indexOf(':') >= 0) {
        prefixStr = prefixStr.split('|')[0];
      }
      prefixStr = prefixStr.replace(/^www\./i, '');

      if (checkUrlFormat(prefixStr, limit)) {
        finalUrl = index > -1 ? `${prefix}/${suffix}` : (prefix as string);
        finalUrl = queryIndex > -1 ? `${finalUrl}?${queryString}` : finalUrl;

        if (!entries || entries.indexOf(finalUrl.toLowerCase()) === -1) {
          entries.push(finalUrl.toLowerCase());
        }
      } else {
        errList.push(newUrl);
      }
    }
  }
  return {
    errList,
    entries,
    exceedLimit,
  };
}
export enum CheckHeaderResult {
  success = 0,
  headerEmpty = 1,
  headerTooLong = 2,
  entriesTooMany = 3,
}
export const checkApprovedHeaderList = function (
  header: ApprovedHeader,
  entries: ApprovedHeader[],
  strLimit = 128,
  entriesLimit = 10
) {
  if (header.key === '' || header.value === '') {
    return CheckHeaderResult.headerEmpty;
  } else if (header.key.length > strLimit || header.value.length > strLimit) {
    return CheckHeaderResult.headerTooLong;
  } else if (entries.length >= entriesLimit) {
    return CheckHeaderResult.entriesTooMany;
  } else {
    return CheckHeaderResult.success;
  }
};

export const sortApprovedHeaderEntries = function (
  header1: ApprovedHeader,
  header2: ApprovedHeader
) {
  return header1.key.localeCompare(header2.key);
};

export const addEmails = (
  email: string[] | string,
  entriesStr: string,
  includeDomain: boolean,
  limit = 256,
  separator = ';',
  listLimit = -1,
  supportWildcard = false
) => {
  let exceedListLimit = false;
  const emails = Array.isArray(email) ? email : [email];
  const emailDomainFormat =
    /^(\*)@([a-zA-Z0-9]([a-zA-Z0-9^_.]{1,64})?([-a-zA-Z0-9]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2,13}$/;
  // const wildcardEmailDomainFormat = /^(\*|[\w.-]+)?@(\*|[\w.-]+\.[\w.-]+)$/;
  const wildcardEmailDomainFormat =
    /^([a-zA-Z0-9!#$%&*'+\-/?^_`{|}~.]{1,64})@([a-zA-Z0-9*]([a-zA-Z0-9*^_.]{1,64})?([-a-zA-Z0-9*]{0,61}[a-zA-Z0-9*])?\.)+[a-zA-Z0-9*]{2,13}$/;
  const wildcardEmailDomain =
    /^([a-zA-Z0-9!#$%&*'+\-/?^_`{|}~.]{1,64})@[a-zA-Z0-9*^_.]{1,64}$/;
  const entries = entriesStr
    .split(separator)
    .filter((entry: string) => entry !== '');
  const errList: string[] = [];
  emails
    .filter((email: string) => email !== '')
    .forEach((email) => {
      if (listLimit !== -1 && entries.length >= listLimit) {
        errList.push(email);
        exceedListLimit = true;
      } else {
        console.log(
          checkDomainFormat(email, limit),
          emailDomainFormat.test(email),
          wildcardEmailDomainFormat.test(email)
        );
        if (checkEmailFormat(email, limit)) {
          if (!entries.includes(email)) {
            entries.push(email);
          }
          console.log(1);
        } else if (
          supportWildcard &&
          email !== '*@*' &&
          (checkDomainFormat(email, limit) ||
            emailDomainFormat.test(email) ||
            wildcardEmailDomainFormat.test(email) ||
            wildcardEmailDomain.test(email))
        ) {
          if (entries && entries.indexOf(email) === -1) {
            entries.push(email);
          }
          console.log(2);
        } else if (
          includeDomain &&
          (emailDomainFormat.test(email) || checkDomainFormat(email, limit))
        ) {
          const domain = email.split('@')[0];
          if (!entries.includes(domain)) {
            entries.push(domain);
          }
          console.log(3);
        } else {
          console.log(4);
          errList.push(email);
        }
      }
    });
  return {
    errList,
    entriesStr: entries.join(separator),
    exceedListLimit,
  };
};

export const removeHeader = (
  header: ApprovedHeader,
  entries: ApprovedHeader[]
) => {
  const newEntries = entries.filter((entry: ApprovedHeader) => {
    if (
      entry.key === header.key &&
      entry.value === header.value &&
      entry.operator === header.operator
    ) {
      return false;
    } else {
      return true;
    }
  });
  return newEntries;
};

export const removeBlockedHeader = (
  header: BlockedHeaderRuleDefinitionsProps[],
  entries: BlockedHeaderRuleDefinitionsProps[][]
) => {
  const newEntries = entries.filter(
    (entry: BlockedHeaderRuleDefinitionsProps[]) => {
      if (isEqual(entry, header)) {
        return false;
      } else {
        return true;
      }
    }
  );
  return newEntries;
};
/*
 * @param allTargets[]
 * @param isPagination: if true, add showMore in the end of allTargets
 * @param getMoreTargets: the function put in showMore
 * @description Add showmore for allTargets
 * */
export const getAllTargetsDisplay = (
  allTargets: any[],
  isPagination: boolean,
  getMoreTargets: () => void
): TreeNode[] => {
  const { t } = i18nInst;
  const allTargetsDisplay = cloneDeep(allTargets);
  if (isPagination) {
    const showMore: TreeNode = {
      title: t(`policy.node.showMore`),
      key: 'showMore',
      disableCheckbox: true,
      onSelect: getMoreTargets,
    };
    const lastItem: TreeNode = allTargetsDisplay.slice(-1)[0];
    if (lastItem.key === 'showMore') {
      allTargetsDisplay.pop();
    }
    allTargetsDisplay.push(showMore);
  }
  return allTargetsDisplay;
};

export function showAddApprovedSenderListErrNotification(errList: string[]) {
  const { t } = i18nInst;
  const notAddedEmails =
    errList.length > 50
      ? `${errList.slice(0, 50).join('\n')}\n...`
      : errList.join('\n');
  const errorContent = `${t('policy.error.totalInvalidEmail')} ${
    errList.length
  } ${t('policy.error.notAddEmails')} ${notAddedEmails}`;

  window.showNotification({
    titleMsg: t('policy.error.invalidEmail'),
    bodyMsg: errorContent,
    severity: 'error',
  });
}
export function showAddFileNamesErrNotification(errList: string[]) {
  const { t } = i18nInst;
  const notAddedFileNames =
    errList.length > 50
      ? `${errList.slice(0, 50).join('\n')}\n...`
      : errList.join('\n');
  const errorContent = `${t('policy.error.invalidFileNameContentTitle')} ${t(
    'policy.error.invalidFileNameContentPre'
  )} ${errList.length} ${t(
    'policy.error.invalidFileNameContentPost'
  )} ${notAddedFileNames}`;

  window.showNotification({
    titleMsg: t('policy.error.invalidFileNameTitle'),
    bodyMsg: errorContent,
    severity: 'error',
  });
}
export function showAddUrlsErrNotification(errList: string[]) {
  const { t } = i18nInst;
  const notAddedUrls =
    errList.length > 50
      ? `${errList.slice(0, 50).join('\n')}\n...`
      : errList.join('\n');
  const errorContent = `${t('policy.error.invalidUrlContentPre')} ${
    errList.length
  } ${t('policy.error.invalidUrlContentPost')} ${notAddedUrls}`;

  window.showNotification({
    titleMsg: t('policy.error.invalidUrlTitle'),
    bodyMsg: errorContent,
    severity: 'error',
  });
}
export function showAddApprovedSenderExceedListLimitNotification(
  limit: number
) {
  const { t } = i18nInst;
  window.showNotification({
    titleMsg: t('policy.error.noApprovedSenderImport'),
    bodyMsg: `${t('policy.error.noImportSenderPre')} ${limit} ${t(
      'policy.error.noImportSenderPost'
    )}`,
    severity: 'error',
  });
}
export function showAddEmailAddressExceedListLimitNotification(limit: number) {
  const { t } = i18nInst;
  window.showNotification({
    titleMsg: t('policy.error.maximumReached'),
    bodyMsg: t('policy.error.noEmailAddress', {
      limit,
    }),
    severity: 'error',
  });
}
// @type: 1-approved, 0-blocked
export function showAddUrlExceedListLimitNotification(limit: number, type = 1) {
  const { t } = i18nInst;
  window.showNotification({
    titleMsg:
      type === 1
        ? t('policy.error.noApprovedUrlImportTitle')
        : t('policy.error.noBlockedUrlImportTitle'),
    bodyMsg: `${t('policy.error.noUrlAddContentPre')} ${limit} ${t(
      'policy.error.noUrlAddContentPost'
    )}`,
    severity: 'error',
  });
}
export function showAddFileNameExceedListLimitNotification(limit: number) {
  const { t } = i18nInst;
  window.showNotification({
    titleMsg: t('policy.error.noFileNameImportTitle'),
    bodyMsg: `${t('policy.error.noFileNameImportContentPre')} ${limit} ${t(
      'policy.error.noFileNameImportContentPost'
    )}`,
    severity: 'error',
  });
}
export function checkDisClaimer(value: string, limit = 512) {
  const { t } = i18nInst;
  // no content
  if (!value) {
    return {
      title: t('policy.error.emptyDisclaimerTitle'),
      content: t('policy.error.emptyDisclaimerContent'),
    };
  }
  // // no tag
  // if (/<[a-z][\s\S]*>/i.test(value)) {
  //   return {
  //     title: t('policy.error.invalidDisclaimerTitle'),
  //     content: t('policy.error.disclaimerNoTagContent'),
  //   };
  // }
  // length limit
  if (value.length > limit) {
    return {
      title: t('policy.error.invalidDisclaimerTitle'),
      content: t('policy.error.disclaimerTooLongContent', { LIMIT: limit }),
    };
  }
  return;
}
export function checkSuspiciousTag(value: string, limit = 200) {
  const { t } = i18nInst;

  if (!value) {
    return {
      title: t('policy.error.emptySuspiciousTagTitle'),
      content: t('policy.error.invalidSuspiciousTagContent'),
    };
  }

  if (value.length > limit) {
    return {
      title: t('policy.error.invalidSuspiciousTagTitle'),
      content: t('policy.error.suspiciousTagTooLong', { LIMIT: limit }),
    };
  }

  return;
}
export function checkActionByType(
  actionValue: string,
  actionType: ActionEnum,
  limit = 512
): ErrorObj | undefined {
  switch (actionType) {
    case ActionEnum.tagSubject:
      return checkSuspiciousTag(actionValue);
    case ActionEnum.addDisclaimer:
      return checkDisClaimer(actionValue, limit);
    default:
      return;
  }
}

export function checkMaxLength(str: string, maxLength: number, msg: any) {
  const { t } = i18nInst;
  if (!str || !maxLength) {
    return;
  }
  if (str.length > maxLength) {
    if (msg.title && msg.content) {
      return msg;
    } else {
      return {
        title: t('policy.error.alertTitle'),
        content: msg,
      };
    }
  }
  return;
}

export function checkEmptyString(str: string, msg: any) {
  const { t } = i18nInst;
  if (!str) {
    if (msg.title && msg.content) {
      return msg;
    } else {
      return {
        title: t('policy.error.alertTitle'),
        content: msg,
      };
    }
  }
  return;
}

export function checkEmailFormatAndEmpty(str: string, msg: any) {
  const { t } = i18nInst;
  const result = {
    title: '',
    content: '',
  };
  const separator = ';';
  const tempStr = str.trim();
  // check empty
  if (!tempStr) {
    if (msg.title && msg.content) {
      result.title = msg.title;
      result.content = msg.content;
    } else {
      result.title = t('policy.error.alertTitle');
      result.content = msg;
    }
    return result;
  }
  // check format
  const reg =
    /^[a-z0-9!#$%&'*+\\/=?^_|}~-]+(?:\.[a-z0-9!#$%&'*+\\/=?^_|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  const emailArr = tempStr
    .trim()
    .split(separator)
    .filter((email) => email.length > 0);
  for (const email of emailArr) {
    if (!reg.test(email)) {
      if (msg.title && msg.content) {
        result.title = msg.title;
        result.content = msg.content;
      } else {
        result.title = t('policy.error.alertTitle');
        result.content = msg;
      }
      return result;
    }
  }
  return;
}

export function checkDlpScanTargetHeader(filter: {
  dpMailHeadersScanEnabled: 0 | 1;
  dpMailHeaders: string;
}) {
  if (!filter.dpMailHeadersScanEnabled) return;
  const { t } = i18nInst;
  const error = {
    dlpScanTargetHeader: {
      title: t('policy.error.invalidMailHeadersTitle'),
      content: t('policy.error.invalidMailHeadersContent'),
    },
  };

  if (filter.dpMailHeaders.trim() === '') return error;
  const headers = filter.dpMailHeaders.split(',');
  if (headers.length > 10) return error;

  const reg = /^[a-zA-Z][a-zA-Z0-9-_]*$/;
  for (const h of headers) {
    if (!reg.test(h)) return error;
  }

  return;
}

type ErrorTitleKey = PolicyFilters & DlpPolicyFilters & 'general';
const formatErrorTitle = (key: ErrorTitleKey, title: string) => {
  const { t } = i18nInst;
  const filterI18nKeyMap: Record<PolicyFilters & DlpPolicyFilters, string> = {
    [PolicyFilters.tmAntispamfilters]:
      'policy.filterName.advancedSpamProtection',
    [PolicyFilters.tmVirusscans]: 'policy.filterName.malwareScanning',
    [PolicyFilters.tmFileblockings]: 'policy.filterName.fileBlocking',
    [PolicyFilters.tmWebthreatprotections]: 'policy.filterName.webReputation',
    [PolicyFilters.tmDdafilters]: 'policy.filterName.virtualAnalyzer',
    [PolicyFilters.tmRiskAiFilters]: 'policy.filterName.riskAi',
    [DlpPolicyFilters.tmDpfilters]: 'policy.filterName.dlp',
    [DlpPolicyFilters.tmKeywordfilters]: 'policy.filterName.keywordExtraction',
    [DlpPolicyFilters.tmSlfFilters]: 'policy.filterName.shareLink',
    general: 'policy.general',
  };
  if (filterI18nKeyMap[key]) {
    const prefix = `[${t(filterI18nKeyMap[key])}]`;
    if (!title.includes(prefix)) {
      return `${prefix}<br />${title}`;
    }
  }
  return title;
};

export function handleValidateError(params: {
  errors: any;
  isValidateAll?: boolean;
}) {
  const { errors, isValidateAll = false } = params;
  const title = (key: ErrorTitleKey, title: string) => {
    return isValidateAll ? formatErrorTitle(key, title) : title;
  };
  if (errors) {
    Object.keys(errors).forEach((key: string) => {
      if (key in PolicyFilters || key in DlpPolicyFilters) {
        // error in a filter
        const filterError = errors[key];
        Object.keys(filterError).forEach((filterKey: string) => {
          if (filterKey !== 'notification') {
            const error = filterError[filterKey];
            window.showNotification({
              titleMsg: title(key as ErrorTitleKey, error.title),
              bodyMsg: error.content,
              severity: 'error',
            });
          } else {
            // filter notification error
            const ntfError = filterError[filterKey];
            Object.keys(ntfError).forEach((ntfKey: string) => {
              const error = ntfError[ntfKey];
              window.showNotification({
                titleMsg: title(key as ErrorTitleKey, error.title),
                bodyMsg: error.content,
                severity: 'error',
              });
            });
          }
        });
      } else {
        // error in general
        const error = errors[key];
        window.showNotification({
          titleMsg: title('general' as ErrorTitleKey, error.title),
          bodyMsg: error.content,
          severity: 'error',
        });
      }
    });
  }
}

export function checkNotificationRecipientGroup(groupId: number, msg: any) {
  const { t } = i18nInst;
  const result = {
    title: '',
    content: '',
  };
  // check empty
  if (!groupId) {
    if (msg.title && msg.content) {
      result.title = msg.title;
      result.content = msg.content;
    } else {
      result.title = t('policy.error.alertTitle');
      result.content = msg;
    }
    return result;
  }
}

export function checkNotification(
  notification: Notification,
  notifyMessageMaxLength = 4095,
  notifySubjectMaxLength = 200
) {
  const { t } = i18nInst;
  const invalidEmailMsgObj = {
    title: t('policy.error.invalidAdminEmailTitle'),
    content: t('policy.error.invalidAdminEmailContent'),
  };
  const invalidRecipientGroupMsgObj = {
    title: t('policy.error.invalidAdminEmailTitle'),
    content: t('policy.error.invalidAdminEmailContent'),
  };
  const invalidMessageObj = {
    title: t('policy.error.invalidMessageTitle'),
    content: t('policy.error.invalidMessageContent', {
      limit: notifyMessageMaxLength,
    }),
  };
  const emptyMessageObj = {
    title: t('policy.error.saveSetting'),
    content: t('policy.error.emptyMessageContent'),
  };
  const invalidSubjectObj = {
    title: t('policy.error.invalidSubjectTitle'),
    content: t('policy.error.invalidSubjectContent', {
      limit: notifySubjectMaxLength,
    }),
  };
  const emptySubjectObj = {
    title: t('policy.error.saveSetting'),
    content: t('policy.error.emptySubjectContent'),
  };
  // check writing style
  // check notification
  // account
  const checkLengthResult = checkMaxLength(
    notification.ntfToAccount,
    config.ui.policy.limitation.notifyToAccountMaxLength,
    invalidEmailMsgObj
  );
  if (checkLengthResult) {
    return {
      notification: { ntfToAccount: checkLengthResult },
    };
  }

  if (notification.ntfRecipientGroupEnabled !== EnableDisableEnum.disabled) {
    // if group enabled, group id can not be empty, 0 means no valid group
    const checkResult = checkNotificationRecipientGroup(
      notification.ntfRecipientGroupId,
      invalidRecipientGroupMsgObj
    );
    if (checkResult) {
      return {
        notification: { ntfRecipientGroupId: checkResult },
      };
    }
  } else {
    // check account format and empty
    const checkResult = checkEmailFormatAndEmpty(
      notification.ntfToAccount,
      invalidEmailMsgObj
    );
    if (checkResult) {
      return {
        notification: { ntfToAccount: checkResult },
      };
    }
  }
  // message
  const messageKeys = ['ntfAdminMessage', 'ntfInternalRecipientMessage'];
  for (const messageKey of messageKeys) {
    let checkResult = checkMaxLength(
      notification[messageKey as keyof Notification] as string,
      notifyMessageMaxLength,
      invalidMessageObj
    );
    if (!checkResult) {
      checkResult = checkEmptyString(
        notification[messageKey as keyof Notification] as string,
        emptyMessageObj
      );
    }
    if (checkResult) {
      return {
        notification: { [messageKey]: checkResult },
      };
    }
  }
  // subject
  const subjectKeys = ['ntfAdminSubject', 'ntfInternalRecipientSubject'];
  for (const subjectKey of subjectKeys) {
    let checkResult = checkMaxLength(
      notification[subjectKey as keyof Notification] as string,
      notifySubjectMaxLength,
      invalidSubjectObj
    );
    if (!checkResult) {
      checkResult = checkEmptyString(
        notification[subjectKey as keyof Notification] as string,
        emptySubjectObj
      );
    }
    if (checkResult) {
      return {
        notification: { [subjectKey]: checkResult },
      };
    }
  }
  return;
}
export function checkReplacementNameOrigin(name: string) {
  const { t } = i18nInst;
  const invalidReplacementNameObj = {
    title: t('policy.error.invalidReplacementNameTitle'),
    content: t('policy.error.invalidReplacementNameContent'),
  };
  const nonDisplayableRegex =
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u0080-\u009F\uFDD0-\uFDEF\uFFFE\uFFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F\uDBBF\uDBFF][\uDFFE\uDFFF]/g;
  const invalidChar = /[/>*?:<"|\\]/;
  if (
    !name ||
    invalidChar.test(name) ||
    nonDisplayableRegex.test(name) ||
    !name.toLocaleLowerCase().endsWith('.txt')
  ) {
    return invalidReplacementNameObj;
  }
  return;
}
export function checkReplacementTextOrigin(text: string, limit = 1023) {
  const { t } = i18nInst;
  const invalidReplacementTextObj = {
    title: t('policy.error.invalidReplacementTextTitle'),
    content: t('policy.error.invalidReplacementTextContent'),
  };
  if (!text || text.length > limit) {
    return invalidReplacementTextObj;
  }
  return;
}

export function uniqueArr(arr: any[]) {
  return Array.from(new Set(arr));
}
export function setNestObjByKeyArr(
  obj: { [key: string]: any },
  keyArr: string[],
  newValue: any
) {
  let nestedObj: any = obj;

  for (let i = 0; i < keyArr.length; i++) {
    const key = keyArr[i];

    if (nestedObj && Object.prototype.hasOwnProperty.call(nestedObj, key)) {
      if (i === keyArr.length - 1) {
        nestedObj[key] = newValue;
      } else {
        nestedObj = nestedObj[key];
      }
    } else {
      return; // 或者根据你的需求进行其他错误处理
    }
  }
}

export const convertUserGroup = (
  users: TargetUser[],
  groups: TargetGroup[],
  policyType?: PolicyTypeEnum
): TreeNode[] => {
  const usersNodes: TreeNode[] = [];
  const groupsNodes: TreeNode[] = [];
  if (users.length > 0) {
    users.forEach((user) => {
      usersNodes.push({
        ...user,
        title: user.title,
        key: user.key,
        icon: {
          name: 'user',
          component: <UserIcon />,
        },
      });
    });
  }
  if (groups.length > 0) {
    const isMsTeams = policyType === PolicyTypeEnum.Teams;
    groups.forEach((group) => {
      const { children } = group;
      let childrenUsers: TargetUser[] = [];
      let childrenGroups: TargetGroup[] = [];
      if (children && children.targets) {
        childrenUsers = children.targets;
      }
      if (children && children.targetGroups) {
        childrenGroups = children.targetGroups;
      }
      groupsNodes.push({
        title: group.title,
        key: group.key,
        icon: {
          name: 'user-team',
          component: isMsTeams ? <TeamsIcon /> : <UserTeamIcon />,
        },
        children: group.children
          ? convertUserGroup(childrenUsers, childrenGroups, policyType)
          : [],
      });
    });
  }
  return [...groupsNodes, ...usersNodes];
};

export const convertToTargets = (
  nodes: TreeNode[]
): { targets: TargetUser[]; targetGroups: TargetGroup[] } | undefined => {
  const result: { targets: TargetUser[]; targetGroups: TargetGroup[] } = {
    targets: [],
    targetGroups: [],
  };
  if (nodes.length === 0) {
    return undefined;
  }
  nodes.forEach((node) => {
    if (node.icon?.name === 'user') {
      result.targets.push({
        title: node.title as string,
        key: node.key as string,
        firstname: node.firstname as string,
        lastname: node.lastname as string,
        email: node.email as string,
      });
    } else if (node.icon?.name === 'user-team') {
      const { children } = node;
      let childrenNodes: TreeNode[] = [];
      if (children && children.length > 0) {
        childrenNodes = children;
      }
      result.targetGroups.push({
        title: node.title as string,
        key: node.key as string,
        children: convertToTargets(childrenNodes),
      });
    }
  });
  return result;
};

export function removeElements<T>(arr: T[], elementsToRemove: T[]): void {
  for (const element of elementsToRemove) {
    const index = arr.indexOf(element);
    if (index !== -1) {
      arr.splice(index, 1);
    }
  }
}
export function findNodeByKey(
  treeData: TreeNode[],
  key: Key
): TreeNode | undefined {
  if (!treeData || !key) {
    return undefined;
  }
  for (const node of treeData) {
    if (node.key === key) {
      return node;
    } else {
      if (Array.isArray(node.children) && node.children.length > 0) {
        const result = findNodeByKey(node.children, key);
        if (result) {
          return result;
        }
      }
    }
  }
}

export function unCheckTargetGroup(data: TreeNode[]) {
  data.forEach((item) => {
    // 检查是否存在 key='all'，如果是则将 disabled 设置为 true
    if (item.key === 'all') {
      item.disableCheckbox = true;
    }

    // 检查是否存在 icon='user-team'，如果是则将 disabled 设置为 true
    if (item.icon?.name === 'user-team') {
      item.disableCheckbox = true;
    }

    // 检查 children 数组的长度是否为 0，如果是则将 disabled 设置为 true
    if (item.children && item.children.length === 0) {
      item.disableCheckbox = true;
    }

    // 其他情况将 disabled 设置为 false
    if (typeof item.disabled === 'undefined') {
      item.disabled = false;
    }

    // 递归处理嵌套的 children 数组
    if (item.children && item.children.length > 0) {
      unCheckTargetGroup(item.children);
    }
  });
  return data;
}

export function disabledAllTargetGroup(data: TreeNode[]) {
  data.forEach((item) => {
    // 检查是否存在 key='all'，如果是则将 disabled 设置为 true
    if (item.key === 'all') {
      item.disabled = true;
    }

    // 检查是否存在 icon='user-team'，如果是则将 disabled 设置为 true
    if (item.icon?.name === 'user-team') {
      item.disabled = true;
    }

    // 检查 children 数组的长度是否为 0，如果是则将 disabled 设置为 true
    if (item.children && item.children.length === 0) {
      item.disabled = true;
    }

    // 其他情况将 disabled 设置为 false
    if (typeof item.disabled === 'undefined') {
      item.disabled = false;
    }

    // 递归处理嵌套的 children 数组
    if (item.children && item.children.length > 0) {
      disabledAllTargetGroup(item.children);
    }
  });
  return data;
}

export function disableCheckboxAllTargetGroup(data: TreeNode[]) {
  data.forEach((item) => {
    // 检查是否存在 key='all'，如果是则将 disableCheckbox 设置为 true
    if (item.key === 'all') {
      item.disableCheckbox = true;
    }

    // 检查是否存在 icon='user-team'，如果是则将 disableCheckbox 设置为 true
    if (item.icon?.name === 'user-team') {
      item.disableCheckbox = true;
    }

    // 检查 children 数组的长度是否为 0，如果是则将 disableCheckbox 设置为 true
    if (item.children && item.children.length === 0) {
      item.disableCheckbox = true;
    }

    // 递归处理嵌套的 children 数组
    if (item.children && item.children.length > 0) {
      disableCheckboxAllTargetGroup(item.children);
    }
  });
  return data;
}

export const handleAllTargetsForTeams = (targets: TreeNode[]) => {
  const availableTargetsGeneralTeams = cloneDeep(targets);
  // remove the children property, so that tree will not show expand icon
  if (availableTargetsGeneralTeams.length) {
    availableTargetsGeneralTeams[0].children?.forEach((item) => {
      item.children = undefined;
    });
  }
  return availableTargetsGeneralTeams;
};

export const handleAllTargetsForExchangeInline = (targets: TreeNode[]) => {
  const availableTargetsGeneral = cloneDeep(targets);
  const availableTargetsGenralInline = disableCheckboxAllTargetGroup(
    availableTargetsGeneral
  );
  return availableTargetsGenralInline;
};

export const checkChangeRecipientAction = (filter: DpFilter) => {
  const rules = filter.tmDpfilterrules;
  const recipients = filter.changeRecipientEmailAddressList;
  const hasAction = rules.some(
    (rule: any) => rule.dpAction === ActionEnum.changeRecipient
  );
  if (hasAction && recipients.trim() === '') {
    const { t } = i18nInst;
    const checkResult = {
      changeRecipientEmailAddressList: {
        title: t(`policy.error.dlp.noneRecipientTitle`),
        content: t(`policy.error.dlp.noneRecipientContent`),
      },
    };
    return checkResult;
  }
  return;
};
export const getServiceTypeFromPolicyType = (policyType: PolicyTypeEnum) => {
  let serviceType;
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
      serviceType = Service.Exchange;
      break;
    case PolicyTypeEnum.ExchangeInline:
      serviceType = Service.ExchangeInline;
      break;
    case PolicyTypeEnum.Gmail:
      serviceType = Service.Gmail;
      break;
    case PolicyTypeEnum.GmailInline:
      serviceType = Service.GmailInline;
      break;
    case PolicyTypeEnum.OneDrive:
      serviceType = Service.OneDrive;
      break;
    case PolicyTypeEnum.SharePoint:
      serviceType = Service.SharePoint;
      break;
    case PolicyTypeEnum.Teams:
      serviceType = Service.Teams;
      break;
    case PolicyTypeEnum.TeamsChat:
      serviceType = Service.TeamsChat;
      break;
    case PolicyTypeEnum.Box:
      serviceType = Service.Box;
      break;
    case PolicyTypeEnum.GoogleDrive:
      serviceType = Service.GoogleDrive;
      break;
    case PolicyTypeEnum.SalesforceSandbox:
      serviceType = Service.SalesforceSandbox;
      break;
    case PolicyTypeEnum.SalesforceProduction:
      serviceType = Service.SalesforceProduction;
      break;
    default:
      break;
  }
  return serviceType;
};
export const checkToken = ({
  keyValue,
  keyName,
}: {
  keyValue: string;
  keyName: string;
}) => {
  const { t } = i18nInst;
  let ret = true;
  const {
    ui: {
      policy: { validTokenList },
    },
  } = config;
  const matchArr = keyValue.match(/%(\w)*%/gi);
  for (const item in matchArr) {
    if (
      validTokenList.includes(matchArr[item as unknown as number].toLowerCase())
    ) {
      continue;
    } else {
      ret = false;
      break;
    }
  }
  if (!ret) {
    return {
      [keyName]: {
        title: t(`policy.error.invalidTokenTitle`),
        content: t(`policy.error.invalidTokenContent`),
      },
    };
  }
  return;
};
const checkReplacementText = ({
  keyValue,
  keyName,
}: {
  keyValue: string;
  keyName: string;
}) => {
  const limit = config.ui.policy.limitation.fileReplacement;
  const result = checkReplacementTextOrigin(keyValue, limit);
  if (result) {
    return {
      [keyName]: result,
    };
  }
  return;
};
const checkReplacementName = ({
  keyValue,
  keyName,
}: {
  keyValue: string;
  keyName: string;
}) => {
  const result = checkReplacementNameOrigin(keyValue);
  if (result) {
    return {
      [keyName]: result,
    };
  }
  return;
};
const validateStradegies = {
  checkNotification,
  checkToken,
  checkReplacementText,
  checkReplacementName,
  checkChangeRecipientAction,
  checkDlpScanTargetHeader,
};
export class FilterValidator {
  filterName: PolicyFilters | DlpPolicyFilters;
  cache: Function[];
  constructor(filterName: PolicyFilters | DlpPolicyFilters) {
    this.filterName = filterName;
    this.cache = [];
  }
  add(value: any, method: keyof typeof validateStradegies) {
    this.cache.push(() => {
      return validateStradegies[method](value);
    });
  }
  check() {
    for (const validatorFunc of this.cache) {
      const errorData = validatorFunc();
      if (errorData) {
        return { [this.filterName]: errorData };
      }
    }
  }
}

function replaceEAINameInPolicyEntity(policy: any) {
  if (policy.policyDescription && policy.policyMode === 3) {
    policy.policyDescription = policy.policyDescription.replace(
      /Email Account Inventory/gm,
      'Email Asset Inventory'
    );
  }
}

export function replaceProductionNameInPolicyEntity<T>(data: T): T {
  let ret = data;
  if (isInUIC()) {
    ret = JSON.parse(
      JSON.stringify(data).replace(
        /Cloud App Security/gm,
        'Cloud Email and Collaboration Protection'
      )
    );
  }
  if (IS_UAE) {
    ret = JSON.parse(
      JSON.stringify(data)
        .replace(/Cloud App Security/gm, 'Cloud Service Security')
        .replace(/Trend Micro/gm, 'Cyber Eye')
    );
  }

  if (Array.isArray(ret)) {
    ret.forEach(replaceEAINameInPolicyEntity);
  } else {
    replaceEAINameInPolicyEntity(ret);
  }

  return ret;
}

export function checkIfPolicyHasEnabledFilter(policy: PolicyItem) {
  // Make sure at least one filter is enabled for ATP and DLP policies
  let hasEnabledFilter: EnableDisableEnum = EnableDisableEnum.disabled;
  const holderType = policy.holderType ?? policy.policyHolderType;
  if (holderType === PolicyHolderTypeEnum.ATP) {
    const _policy = policy as ExchangeOnlinePolicyItem;
    hasEnabledFilter =
      _policy?.tmDdafilters?.ddaEnabled ||
      _policy?.tmVirusscans?.vsEnableVirusscan ||
      _policy?.tmFileblockings?.fbEnabled ||
      _policy?.tmWebthreatprotections?.wtpEnabled ||
      _policy?.tmAntispamfilters?.asEnabled ||
      _policy?.tmRiskAiFilters?.raiEnabled;
  } else if (holderType === PolicyHolderTypeEnum.DLP) {
    const _policy = policy as DlpPolicyItem;
    hasEnabledFilter =
      _policy?.tmDpfilters?.dpEnabled ||
      _policy?.tmKeywordfilters?.kfEnabled ||
      (_policy?.tmSlfFilters?.slEnabled ?? EnableDisableEnum.disabled);
  }
  return hasEnabledFilter;
}

export function checkIfPolicyInTableHasEnabledFilter(
  policy: PolicyItemInTable
) {
  let hasEnabledFilter = false;
  const holderType = policy.holderType ?? policy.policyHolderType;
  if (holderType === PolicyHolderTypeEnum.ATP) {
    hasEnabledFilter =
      policy?.isDdaEnabled ||
      policy?.isVsEnabled ||
      policy?.isFbEnabled ||
      policy?.isWtpEnabled ||
      policy?.isAsEnabled ||
      policy?.isRaiEnabled;
  } else if (holderType === PolicyHolderTypeEnum.DLP) {
    hasEnabledFilter =
      policy?.isDpEnabled || policy?.isKfEnabled || policy?.isSlfEnabled;
  }
  return hasEnabledFilter;
}
